import { Link } from "react-router-dom";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, FileCheck2, Globe2 } from "lucide-react";
import { HOW_IT_WORKS_STEPS, HIW_PILLARS, USE_CASES } from "@/constants/howItWorksContent";

export default function HowItWorks() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const signedIn = user && typeof user === "object";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="how-it-works-page">
      <PublicSiteHeader />

      <main className="flex-1">
        <section className="px-8 lg:px-14 py-16 lg:py-20 border-b border-slate-200">
          <div className="max-w-3xl">
            <div className="eyebrow mb-5">{t("hiw_nav")}</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-[1.05] mb-6 text-balance">
              {t("hiw_title")}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">{t("hiw_sub")}</p>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20 bg-slate-900 text-white border-b border-slate-800">
          <div className="max-w-5xl mx-auto">
            <div className="eyebrow !text-white/60 mb-10">{t("hiw_pillars_eyebrow")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
              {HIW_PILLARS.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <div
                    key={pillar.titleKey}
                    data-testid={`hiw-pillar-${pillar.titleKey}`}
                    className="bg-slate-900 p-8 lg:p-10"
                  >
                    <div className="w-11 h-11 grid-border border-white/20 flex items-center justify-center mb-5">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-serif-display text-2xl mb-3">{t(pillar.titleKey)}</h2>
                    <p className="text-sm text-white/60 leading-relaxed">{t(pillar.descKey)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20 bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto">
            <div className="eyebrow mb-4">{t("hiw_jurisdiction_eyebrow")}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight mb-5 text-balance max-w-3xl">
              {t("hiw_jurisdiction_title")}
            </h2>
            <p className="text-slate-600 leading-relaxed max-w-3xl mb-10">
              {t("hiw_jurisdiction_desc")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              <div className="bg-white p-7 lg:p-8">
                <div className="w-10 h-10 grid-border flex items-center justify-center mb-4">
                  <Globe2 size={17} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif-display text-xl text-slate-900 mb-2">
                  {t("hiw_jurisdiction_country_title")}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("hiw_jurisdiction_country_desc")}
                </p>
              </div>
              <div className="bg-white p-7 lg:p-8">
                <div className="w-10 h-10 grid-border flex items-center justify-center mb-4">
                  <FileCheck2 size={17} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif-display text-xl text-slate-900 mb-2">
                  {t("hiw_jurisdiction_sources_title")}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("hiw_jurisdiction_sources_desc")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20 bg-slate-50 border-b border-slate-200">
          <div className="max-w-5xl mx-auto">
            <div className="eyebrow mb-10">{t("hiw_steps_eyebrow")}</div>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
              {HOW_IT_WORKS_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <li
                    key={step.titleKey}
                    data-testid={`hiw-step-${idx + 1}`}
                    className="flex gap-5"
                  >
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-11 h-11 grid-border border-slate-300 bg-white flex items-center justify-center">
                        <Icon size={18} strokeWidth={1.5} className="text-slate-900" />
                      </div>
                      <div className="font-mono-tight text-[10px] text-slate-400 mt-2">{idx + 1}</div>
                    </div>
                    <div>
                      <h2 className="font-serif-display text-xl text-slate-900 mb-2">{t(step.titleKey)}</h2>
                      <p className="text-sm text-slate-600 leading-relaxed">{t(step.descKey)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20 bg-slate-50 border-b border-slate-200">
          <div className="max-w-5xl mx-auto">
            <div className="eyebrow mb-4">{t("hiw_cases_eyebrow")}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight mb-10 text-balance">
              {t("hiw_cases_title")}
            </h2>
            <div className="space-y-0 grid-border border-slate-200 bg-white">
              {USE_CASES.map((c, idx) => (
                <div
                  key={c.titleKey}
                  data-testid={`hiw-case-${idx + 1}`}
                  className={[
                    "p-8 lg:p-10",
                    idx > 0 ? "border-t border-slate-200" : "",
                  ].join(" ")}
                >
                  <div className="font-mono-tight text-[10px] text-slate-400 mb-2">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif-display text-xl text-slate-900 mb-2">{t(c.titleKey)}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{t(c.descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-8 lg:px-14 py-16 lg:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4 text-balance">
              {t("hiw_cta_title")}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              {signedIn ? t("hiw_cta_workspace_sub") : t("hiw_cta_sub")}
            </p>
            <Button
              asChild
              data-testid="hiw-cta"
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-12 px-8 text-sm gap-3"
            >
              <Link to={signedIn ? "/workspace" : "/login"}>
                {signedIn ? t("hiw_go_workspace") : t("landing_cta")}
                <Arrow size={14} />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="px-8 lg:px-14 py-6 border-t border-slate-200">
        <p className="eyebrow !text-[10px] text-slate-400 text-center">{t("login_footer")}</p>
      </footer>
    </div>
  );
}
